import { logger } from "./logger";
import type { ReportedError } from "./reportError";

/**
 * A way for `utility/reportError` — a plain module function — to raise a React dialog.
 *
 * ⚠️ WHY A BUS AND NOT A PROP. `reportError` is called from 82 catch blocks in 29 files, almost
 * none of which hold a dialog. `ErrorModal` used to be state owned by `JobAction`, so it could
 * only ever be raised by the nine failures that component happens to run; every other failure in
 * the app — every page load, every save, every inspection — had no dialog to be shown in and
 * fell to a toast. Threading a dialog through 82 call sites is not a smaller change than this,
 * it is the same change spread out and impossible to keep consistent.
 *
 * ⚠️ IT IS A `ReportedError` THAT TRAVELS, NOT A STRING. The host needs the `message` for the
 * user, the `question` for the assistant's composer, the `diagnostics` for the escalation
 * e-mail, and the `code` to recognise the same failure arriving twice. Flattening it to text at
 * this boundary is what forced `JobAction` to keep four pieces of state in step by hand.
 *
 * ⚠️ ONE LISTENER, NOT AN ARRAY — the same check `supportAssistantBus` makes, for the same
 * reason. Two hosts means two dialogs over one failure and a coin flip over which one gets it.
 * A single slot makes the second registration visible in the log.
 *
 * ⚠️ `raiseErrorDialog` RETURNS A BOOLEAN AND `reportError` MUST READ IT. `false` means no host
 * is mounted — a unit test rendering one component, a tree that never reached `App.tsx` — and
 * NOTHING HAPPENED. A caller that ignores it silently swallows the failure entirely, which is
 * worse than the toast it replaced. `reportError` falls back to that toast.
 */

export type ErrorDialogListener = (reported: ReportedError) => void;

let listener: ErrorDialogListener | null = null;

/**
 * Register the host. Returns the unsubscribe function, so it drops straight out of a
 * `useEffect`.
 *
 * The unsubscribe clears the slot ONLY if it is still ours: under React 18 StrictMode the effect
 * runs, cleans up and runs again, and an unconditional clear would wipe the second mount's
 * registration with the first mount's cleanup and leave the bus permanently empty.
 */
export function onErrorDialog(fn: ErrorDialogListener): () => void {
  if (listener && listener !== fn) {
    logger.warn(
      "errorDialogBus: a second listener registered. The error dialog is meant to be mounted " +
        "exactly once (ErrorDialogHost in App.tsx); the newest mount now owns the bus."
    );
  }

  listener = fn;

  return () => {
    if (listener === fn) listener = null;
  };
}

/** Is a host there to show a failure? False in unit tests, and before `App` has mounted. */
export function isErrorDialogMounted(): boolean {
  return listener !== null;
}

/**
 * Show this failure in the dialog. Returns `false` when nothing is listening and the failure has
 * NOT been shown — see the warning above; the caller owes the user another surface.
 */
export function raiseErrorDialog(reported: ReportedError): boolean {
  if (!listener) return false;

  try {
    listener(reported);
    return true;
  } catch (error) {
    // Reporting a failure must never become a second failure. Answering `false` sends the caller
    // to the toast, so the user still learns that something went wrong.
    logger.warn("errorDialogBus: the listener threw.", error);
    return false;
  }
}

/** Test seam. Drops the listener. */
export function __resetErrorDialogBus(): void {
  listener = null;
}
